// @ts-nocheck
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface AdvertiserProspectFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  company_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  industry_category: string;
  advertiser_tier: string;
  annual_ad_budget: string;
  target_audience: string[];
  geographic_focus: string[];
  estimated_campaign_value: string;
  preferred_ad_types: string[];
  notes: string;
}

const industryCategories = [
  'Sports Equipment',
  'Athletic Apparel',
  'Nutrition/Supplements',
  'Education',
  'Technology',
  'Financial Services',
  'Healthcare',
  'Automotive',
  'Food & Beverage',
  'Entertainment',
  'Other'
];

const advertiserTiers = [
  { value: 'local', label: 'Local (under $5K)' },
  { value: 'regional', label: 'Regional ($5K-$25K)' },
  { value: 'national', label: 'National ($25K+)' }
];

const targetAudiences = [
  'Youth Sports',
  'High School',
  'College',
  'Coaches',
  'Parents',
  'Athletes',
  'Sports Fans'
];

const adTypes = [
  'Banner Ads',
  'Sponsored Content',
  'Email Sponsorship',
  'Event Partnerships',
  'Social Media',
  'Video Advertising',
  'Newsletter Placement'
];

const AdvertiserProspectForm: React.FC<AdvertiserProspectFormProps> = ({
  onClose,
  onSuccess
}) => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    industry_category: '',
    advertiser_tier: '',
    annual_ad_budget: '',
    target_audience: [],
    geographic_focus: [],
    estimated_campaign_value: '',
    preferred_ad_types: [],
    notes: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('advertiser_prospects')
        .insert({
          sales_rep_id: profile.id,
          company_name: formData.company_name,
          contact_name: formData.contact_name,
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone,
          industry_category: formData.industry_category,
          advertiser_tier: formData.advertiser_tier,
          annual_ad_budget: formData.annual_ad_budget ? parseInt(formData.annual_ad_budget) : null,
          target_audience: formData.target_audience,
          geographic_focus: formData.geographic_focus,
          estimated_campaign_value: formData.estimated_campaign_value ? parseInt(formData.estimated_campaign_value) : null,
          preferred_ad_types: formData.preferred_ad_types,
          notes: formData.notes,
          pipeline_stage: 'prospect'
        });

      if (error) throw error;

      toast.success('Advertiser prospect created successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error creating advertiser prospect:', error);
      toast.error('Failed to create advertiser prospect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field: keyof FormData, value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked
        ? [...(prev[field] as string[]), value]
        : (prev[field] as string[]).filter(item => item !== value)
    }));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Advertiser Prospect</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Company Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="industry_category">Industry Category *</Label>
                <Select 
                  value={formData.industry_category} 
                  onValueChange={(value) => handleInputChange('industry_category', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industryCategories.map(category => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Contact Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_name">Contact Name *</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => handleInputChange('contact_name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => handleInputChange('contact_email', e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="contact_phone">Contact Phone</Label>
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => handleInputChange('contact_phone', e.target.value)}
              />
            </div>
          </div>

          {/* Budget Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Budget Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="advertiser_tier">Advertiser Tier *</Label>
                <Select 
                  value={formData.advertiser_tier} 
                  onValueChange={(value) => handleInputChange('advertiser_tier', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {advertiserTiers.map(tier => (
                      <SelectItem key={tier.value} value={tier.value}>
                        {tier.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="annual_ad_budget">Annual Ad Budget</Label>
                <Input
                  id="annual_ad_budget"
                  type="number"
                  value={formData.annual_ad_budget}
                  onChange={(e) => handleInputChange('annual_ad_budget', e.target.value)}
                  placeholder="Enter amount in USD"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="estimated_campaign_value">Estimated Campaign Value</Label>
              <Input
                id="estimated_campaign_value"
                type="number"
                value={formData.estimated_campaign_value}
                onChange={(e) => handleInputChange('estimated_campaign_value', e.target.value)}
                placeholder="Enter estimated value in USD"
              />
            </div>
          </div>

          {/* Target Audience */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Target Audience</h3>
            <div className="grid grid-cols-3 gap-2">
              {targetAudiences.map(audience => (
                <label key={audience} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.target_audience.includes(audience)}
                    onChange={(e) => handleArrayChange('target_audience', audience, e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">{audience}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Preferred Ad Types */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Preferred Ad Types</h3>
            <div className="grid grid-cols-3 gap-2">
              {adTypes.map(type => (
                <label key={type} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.preferred_ad_types.includes(type)}
                    onChange={(e) => handleArrayChange('preferred_ad_types', type, e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
              placeholder="Additional notes about this prospect..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Creating...' : 'Create Prospect'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdvertiserProspectForm;